import React, { useRef, useEffect, useCallback, useState} from 'react';
import {lstorage} from 'utils/lstorage.js';
import NewPackWindow from "components/packs/NewPackWindow.js";
import {PIANO_ROLL, WAVEFORM, INFO, MODE_TO_HEIGHT} from 'constants/arrangement-modes.js';
import {
    setViewMode,
    setWallpaper,
    setAnimateWallpaper,
    setPullablePartDragging,
    setPackWindowPosition,
    dragEffect,
    setMidiEffectDragging,
    setPulledEffects,
} from 'actions/index.js';
import ipfs from 'decentralized/Ipfs.js';
import {isMobile} from 'react-device-detect';
import Sample from 'repos/Sample.js';
import {useKeyListener} from './useKeyListener.js';
import Modals from './Modals.js';
import Mix from 'components/mix/Mix.js';
import ArrangmentView from 'components/arrangement/ArrangementView.js';
import PreferencesView from 'components/PreferencesView.js';
import SimpleSequencerBottomToolbar from '../installation/SimpleSequencerBottomToolbar.js';
import SidebarComponent from "components/sidebar/SidebarComponent.js";
import StepSequencer from './StepSequencer.js';
import {
    setSequencerEngine,
    setSampleDragging,
    setSampleTagDragging,
    setPresetDragging,
    setTrackDragging,
    setSamplesDragging
} from 'actions/index.js';
import {connect} from 'react-redux';
import {useSequencer} from './useSequencer.js';
import Sticky from 'react-sticky-el';
import StickyScriptComponent from 'components/StickyScriptComponent.js';
import {
    ARRANGEMENT_VIEW ,
    MIX_VIEW,
    MULTI_DETAILS_VIEW,
    PREFERENCES_VIEW ,
    SCRIPTING_VIEW,
    SEQUENCER_VIEW } from "constants/view-modes.js";
import {
    setArrangementBottomHeight,
    setArrangementBottomResizing} from 'actions/index.js';
import SequencerToolbar from 'components/toolbar/SequencerToolbar.js';
import 'react-dropdown/style.css';
import 'bootstrap/dist/css/bootstrap.css';
import BootstrapSlider from 'bootstrap-slider/dist/css/bootstrap-slider.min.css';
import '../../Installation.scss';
import '../../Sequences.css';
import '../../DrumMachineImportMode.css';
import '../../Groups.scss';
import '../../animation.css';
import '../../Preferences.css';
import '../../colors.scss';
import '../../Common.css';
import '../../NFT.scss';

const Sequencer = (props) => { 
    let [ethereumCache, sequencerEngine, ethereumSearcher] = useSequencer(props);
    let [scriptingFocused, setScriptingFocused] = useState(false);
    let [scriptingInput, setScriptingInput] = useState(undefined);
    let [keyListener] = useKeyListener({sequencerEngine: sequencerEngine});

    let scrollAreaClass = props.viewMode === ARRANGEMENT_VIEW ? 'scroll-area arrange' : 'scroll-area';
    let sequencerClassName = props.isTrackFocused ? "sequencer-container focused": "sequencer-container";
    if (props.showPianoRoll && props.viewMode !== ARRANGEMENT_VIEW) {
        sequencerClassName += " show-piano-roll";
    }

    useEffect(() => {
        if (props.showPianoRoll) {
            if (props.bottomMode===PIANO_ROLL) {
                props.setArrangementBottomHeight(window.innerHeight - 40);
            } else  if (props.bottomMode===WAVEFORM) {
                props.setArrangementBottomHeight(350);
            }
        }
    }, [props.bottomMode, props.showPianoRoll]);

    useEffect(() => {
        if (window._isNFT) {
            props.setViewMode(MIX_VIEW);
        }
    }, [sequencerEngine]);

    const onMouseMove = useCallback((e) => {
        let state = sequencerEngine.state;
        if (state.arrangement.isBottomResizing && props.bottomMode !== PIANO_ROLL) {
            let bottomHeight = window.innerHeight - e.pageY + state.arrangement.bottomOffset;
            if (props.viewMode === ARRANGEMENT_VIEW) {
                bottomHeight += 25;
            }
            props.setArrangementBottomHeight(bottomHeight);
        }
        if (state.packs.draggingWindow) {
            props.setPackWindowPosition(
                e.pageX - state.packs.draggingWindow.x,
                e.pageY - state.packs.draggingWindow.y);
        }
    }, [sequencerEngine, props.viewMode, props.showPianoRoll, props.bottomMode]);

    const hasDragging = () => {
        let {instruments, tracks, effects, presets, selector, sequencer} = sequencerEngine.state;
        return instruments.pullablePartDragging ||
            tracks.trackDragging || 
            selector.sampleDragging ||
            selector.samplesDragging ||
            selector.sampleTagDragging;
    };

    const onMouseUp = useCallback((e) => {
        let state = props.store.getState();
        if (state.arrangement.isBottomResizing) {
            props.setArrangementBottomResizing(false);
        }
        if (hasDragging()) {
            clearAllDragging();
        }
    }, [sequencerEngine]);

    const clearAllDragging = () => {
        props.setSampleDragging(undefined);
        props.setPullablePartDragging(undefined);
        props.setSamplesDragging(undefined);
        props.setSampleTagDragging(undefined);
        props.setPresetDragging(undefined);
        props.setMidiEffectDragging(undefined);
        props.setEffectDragging(undefined);
        props.setTrackDragging(undefined);
    };

    const onDrop = useCallback(async (e) => {
        let {instruments, tracks, effects, presets, selector, sequencer} = sequencerEngine.state;

        if (instruments.pullablePartDragging) {
            let part = {...instruments.pullablePartDragging};
            part.x = e.pageX - 5;
            part.y = e.pageY - 100;
            let pulledEffects = [...effects.pulledEffects, part];
            props.setPulledEffects(pulledEffects);
            clearAllDragging();
            return;
        }

        if (tracks.trackDragging) {
            let track = tracks.trackDragging;
            if (track.sequencer) {
                track.sequencer.removeTrack(track);
                clearAllDragging();
                return;
            } else if (track.pad) {
                track.pad.removeTrack(track);
                clearAllDragging();
                return;
            }
        }

        if (selector.sampleDragging !== undefined) {
            for (let pad of sequencerEngine.currentSubSequence.pads) {
                let track = pad.containsSample(selector.sampleDragging);
                if (track) {
                    pad.removeTrack(track);
                    clearAllDragging();
                    return;
                }
            }
            let sampleDragging = selector.sampleDragging;
            sequencerEngine.addSound(sampleDragging, undefined);
            clearAllDragging();
        } else if (selector.samplesDragging) {
            let i=0;
            for (let id of selector.samplesDragging) {
                if (i > 4) {
                    break;
                }
                let sample = id;
                if (typeof id === "string") {
                    sample = new Sample(id);
                    await sample.load();
                }
                sequencerEngine.addSound(sample);
                i++;
            }
            clearAllDragging();
        } else if (selector.sampleTagDragging) {
            sequencerEngine.searchForSounds([selector.sampleTagDragging]);
            clearAllDragging();
        }

        if (presets.presetDragging) {
            sequencerEngine.loadPreset(presets.presetDragging, true);
            clearAllDragging();
        }

        let effectDragging = sequencer.effectDragging ||
            effects.midiEffectDragging;
        if (effectDragging && Object.keys(effectDragging).length > 1) {

            let {effect} = effectDragging;
            effect.x = e.pageX - 5;
            effect.y = e.pageY - 100;
            let pulledEffects = [...effects.pulledEffects, effect];
            props.setPulledEffects(pulledEffects);
            // in this case we need to move the effect to the outside of the
            // track so that it is seen always
            clearAllDragging();
        }
     
    }, [sequencerEngine, props.sampleDragging]);

    useEffect(() => {
        if (window.isNFT) {
            return false;
        }
        let item = lstorage.getItem("wallpaper");
        console.log("item we got from storage=", item);
        if (item) {
            props.setWallpaper(item);
        } else {
            // props.setWallpaper("QmbEKr2mZLWazpwCLEG6BJTRdv1C8LZmxdWLEaLcxiMrMb");
        }
        item  = lstorage.getItem("animateWallpaper");
        if (item !== undefined) {
            props.setAnimateWallpaper(item === "true");
        }
    }, []);

    console.log('rendering sequencer', sequencerEngine);
    if (!sequencerEngine) {
        return (<div></div>);
    }



    let style = {};
    if (props.wallpaper) {
        style.backgroundImage = 'url(' + ipfs.getHashUrl(props.wallpaper) + ')';
        sequencerClassName += " has-wallpaper";
    } else {
        sequencerClassName += " no-wallpaper";
    }
    if (props.animateWallpaper) {
        sequencerClassName += " animate";
    }

    if (isMobile) {
        sequencerClassName += " mobile";
    }
    sequencerClassName += " " + props.viewMode;
    if (window._isNFT) {
        sequencerClassName += " " + "NFT";
    }
    return (
        <div
          onDrop={onDrop}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          style={style}
          onDragOver={e => e.preventDefault()}
          className={sequencerClassName}>
	  <div className={scrollAreaClass}>
          {!(props.showPianoRoll && props.viewMode === SEQUENCER_VIEW) && !props.wallpaper ? 
             <div color="radial-gradient(75.29% 75.29% at 64.96% 24.36%, #F0DFA7 0.52%, #FDBDAA 18.75%, #FEA186 34.38%, #F09277 49.48%, #D27584 74.48%, #6B7B8A 100%)" className="coke-machine-glow"/>
             : ''}
            {props.creatingNewPack || props.packToEdit ? <NewPackWindow json={props.packToEdit}/> : ''}
            <Sticky
              stickyClassName="toolbar-sticky"
              className=""
              mode="top"
              boundaryElement=".main-area"
              hideOnBoundaryHit={false}
              topOffset={-25}
              bottomOffset={0}>
              <SequencerToolbar/>
            </Sticky>
            <div className="main-area">
              <Modals sequencerEngine={sequencerEngine}/>
              {props.viewMode === SEQUENCER_VIEW && !(props.showPianoRoll && props.bottomMode === PIANO_ROLL) ?
               <StepSequencer
                 onDrop={onDrop}
                 isTrackFocused={props.isTrackFocused}
                 viewMode={props.viewMode}
                 sequencerEngine={sequencerEngine}/> :
               props.viewMode === MIX_VIEW ?
               <Mix sequencerEngine={sequencerEngine}/> :
               props.viewMode === ARRANGEMENT_VIEW ? 
               <ArrangmentView
                 sequencerEngine={sequencerEngine}/> :
               props.viewMode === PREFERENCES_VIEW ?
               <PreferencesView
                 midiEngine={sequencerEngine.midi}
                 channelMixer={sequencerEngine.channelMixer}
                 sequencerEngine={sequencerEngine}/> :
               ''}
              {!(props.showPianoRoll && props.bottomMode===PIANO_ROLL) && <SidebarComponent sequencerEngine={sequencerEngine} ethereumSearcher={ethereumSearcher}/>}
            </div>
          </div>
            {props.showPianoRoll && props.viewMode === SEQUENCER_VIEW ? 
             <SimpleSequencerBottomToolbar sequencerEngine={sequencerEngine}/> :
             props.viewMode === SEQUENCER_VIEW ? 
             <StickyScriptComponent
               sequencerEngine={sequencerEngine}
               onSetInput={setScriptingInput}
               update={() => []}
               showScriptingView={() => []}
               scripting={sequencerEngine.scripting}
               scriptingFocused={scriptingFocused}/> : ''
            }
        </div>
    );
}

const mapStateToProps = (state, props) => {
  return {
      viewMode: state.sequencer.viewMode,
      isTrackFocused: state.sequencer.trackFocused !== undefined,
      showPianoRoll: state.sequencer.showPianoRoll,
      bottomMode: state.arrangement.bottomMode,
      wallpaper: state.sequencer.wallpaper,
      animateWallpaper: state.sequencer.animateWallpaper,
      creatingNewPack: state.packs.creatingNewPack,
      packToEdit: state.packs.packToEdit
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
      setSequencerEngine: sequencerEngine => dispatch(setSequencerEngine(sequencerEngine)),
      setArrangementBottomHeight: (height) =>
            dispatch(setArrangementBottomHeight(height)),
      setArrangementBottomResizing: (resizing) =>
          dispatch(setArrangementBottomResizing(resizing)),
      setSamplesDragging: (s) => dispatch(setSamplesDragging(s)),
      setViewMode: (s) => dispatch(setViewMode(s)),
      setSampleDragging : sample => dispatch(setSampleDragging(sample)),
      setWallpaper: w => dispatch(setWallpaper(w)),
      setAnimateWallpaper: w => dispatch(setAnimateWallpaper(w)),
      setSampleTagDragging: x => dispatch(setSampleTagDragging(x)),
      setPresetDragging : x => dispatch(setPresetDragging(x)),
      setPackWindowPosition: (x,y) => dispatch(setPackWindowPosition(x, y)),
      setPulledEffects: e => dispatch(setPulledEffects(e)),
      setEffectDragging: fx => dispatch(dragEffect(fx)),
      setMidiEffectDragging: fx => dispatch(setMidiEffectDragging(fx)),
      setPullablePartDragging: fx => dispatch(setPullablePartDragging(fx)),
      setTrackDragging: fx => dispatch(setTrackDragging(fx)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Sequencer);
